import { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import getSitesForStaticPaths from 'helpers/getSitesForStaticPaths'
import BOARD_QUERY from 'graphql/board.query'
import BOARD_MENU_QUERY from 'graphql/boardMenu.query'
import BOARD_PARENT_QUERY from 'graphql/boardParent.query'
import MODELS_QUERY from 'graphql/models.query'

import Layout from 'components/Layout'
import WordpressSEO from 'components/WordpressSEO'
import ModelList from 'components/ModelList'
import BoardsMenu from 'components/BoardsMenu'
import BoardsAlphabeticalFilter from 'components/BoardsAlphabeticalFilter'
import SliderCover from 'components/SliderCover/dynamic'

import s from './board.module.css'
import StateInitializer from '../../../../components/StateInitializer'
import getGlobalSettings from '../../../../helpers/getGlobalSettings'
import getSites from '../../../../helpers/getSites'
import { createApolloClient } from '../../../../lib/createApolloClient'
import * as Sentry from '@sentry/nextjs'
import useHasChanged from 'hooks/useHasChanged'

function Board({
  globalSettings,
  sites,
  siteSlug,
  title = '',
  seo = {},
  acfModelBoardsCover = {},
  boards = [],
  boardUri = '',
  boardType = '',
  boardSlug = '',
  site = '',
  models = [],
}) {
  const covers = acfModelBoardsCover?.highlightOrder?.nodes || null

  const boardUrl = `${boardType}${boardSlug ? `/${boardSlug}` : ''}`
  const [boardToggled, setBoardToggled] = useState(false)
  const [showFollowers, setShowFollowers] = useState(false)
  const [inTownOnly, setInTownOnly] = useState(false)
  const [activeFilter, setActiveFilter] = useState(null)

  const handleBoardToggle = () => {
    setBoardToggled(!boardToggled)
  }

  const handleFollowersToggle = () => {
    setShowFollowers(!showFollowers)
  }

  // Adjust state during render (rather than in an effect) when
  // navigating to a different board.
  if (useHasChanged(boardUrl)) setActiveFilter(null)

  const setBoardsMenu = (classname = '') => {
    return (
      <BoardsMenu
        className={classname}
        currentBoard={boardSlug}
        boards={boards}
        site={site}
        boardType={boardType}
        boardToggled={boardToggled}
        handleBoardToggle={handleBoardToggle}
        inTownToggled={inTownOnly}
        toggleInTown={() => setInTownOnly(!inTownOnly)}
        handleFollowersToggle={handleFollowersToggle}
      />
    )
  }

  return (
    <StateInitializer
      globalSettings={globalSettings}
      sites={sites}
      siteSlug={siteSlug}
    >
      <Layout globalSettings={globalSettings}>
        <WordpressSEO
          url={`/${site}/${boardType}/${boardSlug}`}
          seo={seo}
          title={title}
        />
        <div
          className={classNames(s.container, {
            [s.containerTopSpacing]: !covers,
          })}
        >
          {setBoardsMenu(s.boardsMenuMobile)}
          {covers && <SliderCover className={s.slider} covers={covers} />}
          {setBoardsMenu(s.boardsMenu)}
          <ModelList
            group={boardType}
            boardSlug={boardSlug}
            models={
              activeFilter || inTownOnly
                ? models.filter((m) => {
                    if (inTownOnly && !m.inTown) return false
                    if (!activeFilter) return true
                    return m.firstName.charAt(0).toUpperCase() === activeFilter
                  })
                : models
            }
            showFollowers={showFollowers}
          />
          {models.length > 0 && (
            <BoardsAlphabeticalFilter
              models={models}
              activeFilter={activeFilter}
              setFilter={(f) => setActiveFilter(f)}
            />
          )}
        </div>
      </Layout>
    </StateInitializer>
  )
}

Board.propTypes = {
  globalSettings: PropTypes.object.isRequired,
  sites: PropTypes.array.isRequired,
  siteSlug: PropTypes.string.isRequired,
  seo: PropTypes.object,
  boards: PropTypes.array,
  boardUri: PropTypes.string,
  boardType: PropTypes.string,
  boardSlug: PropTypes.string,
  site: PropTypes.string, // TODO possibly consolidate with siteSlug if they're the same
}

export default Board

export async function getStaticProps({ params }) {
  const { site, boardType, boardSlug } = params

  const apolloClient = createApolloClient()

  Sentry.addBreadcrumb({
    category: 'getStaticProps',
    message: `Loading Board props for site ${site}, boardType ${boardType}, boardSlug ${boardSlug}`,
    level: 'info',
    data: { params },
  })

  // boardSlug is a mandatory URL segment for this route -- always truthy, so
  // boardUri === boardSlug always. The old `parent?.acfModelBoardParent
  // .departmentOrder?.[0]?.slug` fallback was dead code and required waiting
  // on BOARD_PARENT_QUERY for no reason; BOARD_QUERY/MODELS_QUERY don't need
  // `parent` at all, only the `boards` menu prop does.
  const boardUri = boardSlug

  const [
    globalSettings,
    sites,
    { data: parentData, error: parentError },
    { data, error },
    { data: agencyPinData, error: agencyPinError },
  ] = await Promise.all([
    getGlobalSettings(site),
    getSites(),
    apolloClient
      .query({
        query: BOARD_PARENT_QUERY,
        context: { site },
        variables: { boardUri: boardType },
      })
      .catch((e) => ({ data: { parent: null }, error: e })),
    apolloClient
      .query({
        query: BOARD_QUERY,
        context: { site },
        variables: { boardUri: `${boardType}/${boardUri}` },
      })
      .catch((e) => ({ data: null, error: e })),
    apolloClient
      .query({
        query: MODELS_QUERY,
        context: { site: 'solarnet' },
        variables: { boardType, boardUri, site },
      })
      .catch((e) => ({ data: null, error: e })),
    // Each query above catches its own failure independently (matching the
    // pattern used in pages/[site]/model/[modelSlug].js and
    // [boardType]/[boardSlug]/[modelSlug]/index.js), so one query failing
    // doesn't discard the other four's successfully-resolved results. This
    // outer .catch() is now just a backstop for getGlobalSettings/getSites
    // throwing something unexpected, not the primary error-handling path.
  ]).catch((e) => {
    Sentry.captureException(new Error(`Error loading Board props - ${e}`))
    console.error(`Error loading Board props - ${e}`)
    return [
      undefined,
      undefined,
      { data: { parent: null }, error: e },
      { data: null, error: e },
      { data: null, error: e },
    ]
  })

  if (parentError) {
    Sentry.captureException(
      new Error(`Error loading Board props - ${parentError}`)
    )
    console.error(`Error loading Board parent props - ${parentError}`)
  }

  const { parent } = parentData
  const boards = parent?.acfModelBoardParent?.departmentOrder?.nodes || null

  if (error) {
    Sentry.captureException(new Error(`Error loading Board props - ${error}`))
    console.error(`Error loading Board props - ${error}`)
  }

  if (!data) {
    return null
  }

  if (agencyPinError) {
    Sentry.captureException(
      new Error(`Error loading Board AgencyPin props - ${agencyPinError}`)
    )
    console.error(`Error loading Board AgencyPin props - ${agencyPinError}`)
  }

  if (!agencyPinData) {
    return null
  }

  return {
    props: {
      ...params,
      ...data.board,
      ...agencyPinData,
      boards,
      globalSettings,
      sites,
      siteSlug: site,
    },
    revalidate: 60 * 60, // Revalidate every hour
  }
}

export async function getStaticPaths() {
  const paths = []

  await getSitesForStaticPaths(
    ({ site }) => ({
      query: BOARD_MENU_QUERY,
      context: { site },
    }),
    (data, { site }) =>
      data.modelBoards.nodes.forEach((board) =>
        (board.acfModelBoardParent.departmentOrder?.nodes || []).map((b) =>
          paths.push({
            params: {
              site,
              boardType: board.slug,
              boardSlug: b.slug,
            },
          })
        )
      )
  )

  return {
    paths,
    fallback: 'blocking',
  }
}
